import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { samplesApi } from "@/services/api";
import type { FireExperimentUpsert, FireTestListParams } from "@/types";

/**
 * 모의현장 DB (PLAN 2-11) — 시료에 '화재시험 조건'이 입력된 자료만 모은다.
 * 조건 입력·해제 뒤에는 목록과 그 시료의 조건을 함께 새로 읽는다.
 */
export function useFireTests(params: FireTestListParams) {
  return useQuery({
    queryKey: ["fire-tests", params],
    queryFn: async () => (await samplesApi.listFireTests(params)).data,
    placeholderData: keepPreviousData,
  });
}

/** 재질·인화성 액체별 시료 수 — 목록 API 보다 가볍다(MS 총계를 계산하지 않음). */
export function useFireTestFacets() {
  return useQuery({
    queryKey: ["fire-tests", "facets"],
    queryFn: async () => (await samplesApi.getFireTestFacets()).data,
  });
}

export function useFireExperiment(sampleId: number | undefined) {
  return useQuery({
    queryKey: ["samples", "fire-experiment", sampleId],
    queryFn: async () => {
      if (!sampleId) return null;
      return (await samplesApi.getFireExperiment(sampleId)).data;
    },
    enabled: !!sampleId,
  });
}

function useInvalidateFireTests(sampleId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["fire-tests"] });
    queryClient.invalidateQueries({
      queryKey: ["samples", "fire-experiment", sampleId],
    });
  };
}

export function useUpsertFireExperiment(sampleId: number) {
  const invalidate = useInvalidateFireTests(sampleId);
  return useMutation({
    mutationFn: async (data: FireExperimentUpsert) =>
      (await samplesApi.upsertFireExperiment(sampleId, data)).data,
    onSuccess: invalidate,
  });
}

export function useDeleteFireExperiment(sampleId: number) {
  const invalidate = useInvalidateFireTests(sampleId);
  return useMutation({
    mutationFn: async () => samplesApi.deleteFireExperiment(sampleId),
    onSuccess: invalidate,
  });
}
